import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import MagneticButton from './MagneticButton'

const generateParticles = () =>
	Array.from({ length: 50 }, (_, i) => ({
		id: i,
		left: Math.random() * 100,
		top: Math.random() * 100,
		size: Math.random() * 1.8 + 0.4,
		delay: Math.random() * 6,
		duration: Math.random() * 4 + 4,
		opacity: Math.random() * 0.25 + 0.04,
	}))

const WelcomeSection = () => {
	const [displayText, setDisplayText] = useState('')
	const [currentIndex, setCurrentIndex] = useState(0)
	const [typingDone, setTypingDone] = useState(false)
	const fullText = "I'm Zakarya"

	const sectionRef = useRef(null)
	const spotlightRef = useRef(null)
	const textParallaxRef = useRef(null)
	const imageParallaxRef = useRef(null)
	const orb1Ref = useRef(null)
	const orb2Ref = useRef(null)

	const particles = useMemo(() => generateParticles(), [])

	// Typewriter
	useEffect(() => {
		if (currentIndex < fullText.length) {
			const t = setTimeout(() => {
				setDisplayText((prev) => prev + fullText[currentIndex])
				setCurrentIndex((prev) => prev + 1)
			}, 100)
			return () => clearTimeout(t)
		} else {
			setTypingDone(true)
		}
	}, [currentIndex])

	// Mouse spotlight — direct DOM, no re-renders
	const handleMouseMove = useCallback((e) => {
		if (!spotlightRef.current || !sectionRef.current) return
		const rect = sectionRef.current.getBoundingClientRect()
		const x = e.clientX - rect.left
		const y = e.clientY - rect.top
		spotlightRef.current.style.background = `radial-gradient(700px circle at ${x}px ${y}px, rgba(59,130,246,0.1), rgba(6,182,212,0.03) 40%, transparent 65%)`
	}, [])

	// Parallax on scroll — direct DOM
	useEffect(() => {
		const onScroll = () => {
			const y = window.scrollY
			if (textParallaxRef.current) {
				textParallaxRef.current.style.opacity = String(Math.max(0, 1 - y / 450))
				textParallaxRef.current.style.transform = `translateY(${y * 0.18}px)`
			}
			if (imageParallaxRef.current) {
				imageParallaxRef.current.style.transform = `translateY(${y * 0.09}px)`
			}
			if (orb1Ref.current) {
				orb1Ref.current.style.transform = `translate(${y * 0.06}px, ${y * 0.12}px)`
			}
			if (orb2Ref.current) {
				orb2Ref.current.style.transform = `translate(${-y * 0.06}px, ${-y * 0.1}px)`
			}
		}
		window.addEventListener('scroll', onScroll, { passive: true })
		return () => window.removeEventListener('scroll', onScroll)
	}, [])

	return (
		<section
			ref={sectionRef}
			className='relative min-h-screen flex items-center justify-center overflow-hidden'
			onMouseMove={handleMouseMove}>
			{/* Base background */}
			<div className='absolute inset-0 bg-[#030712]' />

			{/* Mouse spotlight */}
			<div
				ref={spotlightRef}
				className='absolute inset-0 pointer-events-none'
				style={{ willChange: 'background' }}
			/>

			{/* Ambient orbs */}
			<div
				ref={orb1Ref}
				className='absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-blue-600/8 rounded-full blur-3xl pointer-events-none'
				style={{ willChange: 'transform' }}
			/>
			<div
				ref={orb2Ref}
				className='absolute bottom-1/3 right-1/4 w-[400px] h-[400px] bg-cyan-500/8 rounded-full blur-3xl pointer-events-none'
				style={{ willChange: 'transform', animation: 'glowPulse 4s 1.5s ease-in-out infinite' }}
			/>

			{/* Grid pattern */}
			<div
				className='absolute inset-0 opacity-[0.025] pointer-events-none'
				style={{
					backgroundImage:
						'linear-gradient(rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.15) 1px, transparent 1px)',
					backgroundSize: '60px 60px',
				}}
			/>

			{/* Floating particles */}
			<div className='absolute inset-0 pointer-events-none overflow-hidden'>
				{particles.map((p) => (
					<div
						key={p.id}
						className='absolute rounded-full bg-blue-400'
						style={{
							left: `${p.left}%`,
							top: `${p.top}%`,
							width: `${p.size}px`,
							height: `${p.size}px`,
							opacity: p.opacity,
							animation: `float ${p.duration}s ${p.delay}s ease-in-out infinite`,
						}}
					/>
				))}
			</div>

			{/* Content */}
			<div className='relative z-10 max-w-6xl mx-auto px-6 lg:px-12 w-full pt-20'>
				<div className='flex flex-col lg:flex-row items-center gap-12 lg:gap-20'>
					{/* Text — parallax wrapper */}
					<div
						ref={textParallaxRef}
						className='flex flex-col gap-6 flex-1 text-center lg:text-left'
						style={{ willChange: 'transform, opacity' }}>
						<div
							className='text-blue-400 font-mono text-xs tracking-[0.25em] uppercase opacity-0'
							style={{ animation: 'fadeIn 0.7s 0.2s ease-out forwards' }}>
							Full Stack Developer
						</div>

						<h1
							className='text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight opacity-0'
							style={{ animation: 'fadeIn 0.7s 0.4s ease-out forwards' }}>
							<span className='text-white'>Hello, </span>
							<span className='bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent'>
								{displayText}
							</span>
							<span
								className='text-blue-400'
								style={{
									animation: typingDone ? 'pulse 1s ease-in-out infinite' : 'none',
								}}>
								|
							</span>
						</h1>

						<p
							className='text-gray-400 text-base lg:text-xl max-w-xl mx-auto lg:mx-0 leading-relaxed opacity-0'
							style={{ animation: 'fadeIn 0.7s 0.6s ease-out forwards' }}>
							A multidisciplinary{' '}
							<span className='text-blue-400 font-semibold'>Full Stack Developer</span>{' '}
							with a passion for creating engaging, elegant{' '}
							<span className='text-cyan-400 font-semibold'>web applications.</span>
						</p>

						<p
							className='text-gray-500 text-sm lg:text-base opacity-0'
							style={{ animation: 'fadeIn 0.7s 0.75s ease-out forwards' }}>
							Currently building clinical trial technology at{' '}
							<span className='text-blue-400/90 font-medium'>Clinibase</span> —
							Australia's largest clinical trials company.
						</p>

						<div
							className='flex gap-4 justify-center lg:justify-start mt-2 opacity-0'
							style={{ animation: 'fadeIn 0.7s 0.9s ease-out forwards' }}>
							<MagneticButton
								href='#projects'
								className='px-8 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-blue-500/30 text-sm'>
								View My Work
							</MagneticButton>
							<MagneticButton
								href='#contact'
								className='px-8 py-3 border border-white/20 text-gray-300 font-semibold rounded-full hover:bg-white/5 hover:border-white/40 transition-colors duration-300 text-sm'>
								Contact Me
							</MagneticButton>
						</div>
					</div>

					{/* Profile image — separate parallax rate */}
					<div
						ref={imageParallaxRef}
						className='relative flex-shrink-0 opacity-0'
						style={{
							animation: 'fadeIn 0.9s 0.5s ease-out forwards',
							willChange: 'transform',
						}}>
						<div className='w-56 h-56 sm:w-64 sm:h-64 lg:w-80 lg:h-80 relative animate-float'>
							<div className='absolute -inset-3 bg-gradient-to-br from-blue-500/25 to-cyan-500/25 rounded-full blur-2xl' />
							{/* Spinning conic ring */}
							<div
								className='absolute -inset-1 rounded-full'
								style={{
									background:
										'conic-gradient(from 0deg, rgba(59,130,246,0.7), rgba(6,182,212,0.5), rgba(139,92,246,0.3), transparent, rgba(59,130,246,0.7))',
									animation: 'spin 7s linear infinite',
								}}
							/>
							<div className='absolute inset-0.5 bg-[#030712] rounded-full' />
							<img
								src='/images/man.png'
								alt='Zakarya'
								className='absolute inset-1 w-[calc(100%-8px)] h-[calc(100%-8px)] object-cover rounded-full z-10'
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Scroll indicator */}
			<div className='absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2'>
				<div className='w-px h-10 bg-gradient-to-b from-blue-500/60 to-transparent animate-bounce-slow' />
				<span className='text-gray-600 text-xs tracking-[0.2em] uppercase'>Scroll</span>
			</div>
		</section>
	)
}

export default WelcomeSection
